<template>
  <v-card id="activity-form">
    <v-dialog
      v-model="costWarning"
      persistent
      width="400"
    >
      <ActivityCostWarning
        v-if="costWarning"
        :activityCost="activity.baseCost?.rate"
        @confirmation="handleWarningResult"
      />
    </v-dialog>

    <v-card-title :class="contractTypeColor">
      <span class="headline mr-4">{{ title }}</span>
      <ActivityFormContractHeader :contract="contract" />
      <Icon
        iconColor="white"
        icon="mdi-close"
        dataTestId="activity-close"
        :small="false"
        :large="true"
        :tooltipText="$t('close')"
        @icon-clicked="$emit('close-form')"
      />
    </v-card-title>

    <v-card-text class="mt-4">
      <FormWrapper
        testId="save-activity"
        :disabled="isButtonDisabled"
        :buttonColor="contractTypeColor"
        @submit="saveChanges"
      >
        <v-container fluid grid-list-sm>
          <TemplateAutocomplete
            id="activity-activity-template"
            :templateId="activity.activityTemplate ? activity.activityTemplate.activityTemplateId : undefined"
            :focus.sync="templateError"
            :includeReceivables="contractType !== ContractType.Transfer.value"
            :includeTransferActivities="contractType === ContractType.Transfer.value"
            :includeDefectActivities="contractType === ContractType.Production.value || contractType === ContractType.WoodsSale.value"
            :accountingCategoryMutable="activityTemplateMutable"
            includeModifiers
            :includeDepletionActivities="includeDepletion"
            :distance="contract.distance"
            @template-chosen="templateChosen"
          />
          <v-row v-if="isDistanceTraveledActivity" dense>
            <v-col cols="12" lg="5" offset-lg="7" class="mt-n2">
              <v-chip
                small
                label
                color="info"
                text-color="white"
                data-testid="activity-distance-traveled-indicator">
                <v-icon left small>mdi-map-marker-distance</v-icon>
                {{ distanceTraveledIndicatorText }}
              </v-chip>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="4" v-if="showRecoveryModeSelection">
              <v-autocomplete
              v-model="activity.recoveryMode"
              :label="$t('recoveryMode')"
              return-object
              :items="mappedRecoverymodes"
              item-text="name"
              @change="activity.advanceId = undefined">
                <template #item="{item}">
                  {{ item.name }}
                  <v-spacer/>
                  <v-tooltip bottom color="primary" v-if="!item.disabled && item.definition">
                    <template #activator="{on}">
                      <v-icon right color="primary" v-on="on" small>
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span class="subtitle-1 white--text" style="white-space: pre-line" disabled>
                      {{ $t(item.definition, { tract: contract.tract })}}
                    </span>
                  </v-tooltip>
                </template>
                <template #append-outer>
                  <Icon
                  v-if="activity.recoveryMode.value === RecoveryMode.None.value && activity.contractor?.hasAdvances"
                  icon="mdi-alert"
                  iconColor="warning"
                  :small="false"
                  :tooltipText="$t('counterpartyHasOutstandingAdvance')"/>
                </template>
              </v-autocomplete>
            </v-col>
            <v-spacer/>
            <v-col cols="12" sm="6">
              <span v-if="activityIsRuntimeCounterparty">
                <v-text-field :value="counterpartyString" :label="$t('counterparty')" readonly outlined color="primary">
                  <template #prepend-inner>
                    <Icon
                    icon="mdi-lock-open"
                    icon-color="byproduct"
                    :tooltipText="$t('switchToConcreteCounterparty')"
                    :small="false"
                    @icon-clicked="activity.runtimeCounterparty = null"/>
                    <Icon
                    :icon="dynamicCounterpartyNotSet ? 'mdi-alert' : 'mdi-alpha-r-circle-outline'"
                    :small="false"
                    :tooltipText="runtimeCounterparty(activity.runtimeCounterparty)"
                    :iconColor="dynamicCounterpartyNotSet ? 'error' : 'info'"/>
                  </template>
                </v-text-field>
              </span>
              <AccountAutocomplete
                v-else
                class="ml-3"
                userSetting="activityAccount"
                id="activity-account"
                :accountId="activity?.contractor?.accountId"
                :focus.sync="accountError"
                @account-chosen="accountChosen">
                  <template #prepend v-if="propActivity?.runtimeCounterparty !== null && propActivity?.runtimeCounterparty !== undefined && activity.activityTemplate?.category !== AccountingCategory.Accrual.value">
                    <Icon
                    icon="mdi-arrow-u-left-top-bold"
                    iconColor="black"
                    :tooltipText="$t('switchToRuntimeCounterparty')"
                    :small="false"
                    @icon-clicked="switchToRuntimeCounterparty()"/>
                  </template>
              </AccountAutocomplete>
            </v-col>
          </v-row>
          <v-row v-if="showRecoveryModeSelection">
            <v-col cols="12">
              <AdvanceAutocomplete
              v-if="activity.recoveryMode.value === RecoveryMode.Specific.value"
              :advanceId="activity.advanceId"
              :accountId="activity?.contractor?.accountId"
              @advance-chosen="advanceChosen"
              :filter="a => a.recoveryStrategy === RecoveryStrategy.Ticket.value && recoveryTypeAllowedForPayBy(a.recoveryType, activity.baseCost?.payBy)"
            />
            </v-col>
          </v-row>

          <v-row v-if="canBeDeducted">
            <v-col cols="12">
              <span class="title">{{ $t('paymentDeductions') }}</span>
            </v-col>
            <v-col cols="11" class="mt-n5">
              <v-autocomplete
              :label="$t('deductingActivities')"
              :items="deductionEligibleActivities"
              :item-value="getItemValue"
              multiple
              chips
              v-model="activity.deductingActivities"
              :item-text="deductingActivityText"
              return-object/>
            </v-col>
          </v-row>

          <v-row v-if="showBaseCost">
            <v-col cols="12" class="mt-n3">
              <span class="title">{{$t('baseCost')}}</span>
            </v-col>

            <v-col cols="12">
              <BaseCostForm
                id="activity-base-cost"
                :propBaseCost="activity.activityTemplate?.baseCost"
                :accountingCategory="activity?.activityTemplate?.category"
                :editing="true"
                :isBaseCostForm="true"
                :expectedValue="rateStats?.baseRecentAverage"
                :isLogsContract="contractType === ContractType.Production.value || contractType === ContractType.WoodsSale.value"
                :contract="contract"
                allowRateSheet
                :accountId="contract.destinationAccountId"
                :activity="activity"
                :payByTypes="baseCostPayByTypes"
                @base-cost-changed="baseCostChanged"
              />
            </v-col>
          </v-row>
        </v-container>
        <template #left-action v-if="propActivity?.deductFromActivityId != null">
          <v-container>
            <v-row>
              <v-col cols="12">
                <Icon
                icon="mdi-circle-multiple-outline"
                :small="false"
                :tooltipText="$t('deducts')"
                icon-color="success"/>
                <span class="subtitle-1">
                  {{ deductionText }}
                </span>
              </v-col>
            </v-row>
          </v-container>
        </template>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import * as FieldRules from '@/utils/rules'
import {
  AccountingCategory,
  ActivityModifier,
  FinancialIntegrationType,
  ContractType, RuntimeCounterparty,
  RecoveryMode,
  TemplateSpecialization,
  RecoveryStrategy,
  TemplateAccountingCategory,
  CostType,
  TractTypeCategory,
  TractTypeSpecialization,
  NamedRateSpecialization,
  PayBy
} from '@/utils/Enumerations'
import { mapActions, mapGetters } from 'vuex'
import { colorClassForContractType } from '@/utils/componentHelpers.js'
import { recoveryTypeAllowedForPayBy } from '@/utils/FinancialHelpers.js'
import { TemplateSpecializationInfo } from '@/components/activity/ActivityMapping'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import BaseCost from '@/model/BaseCost.js'

export default {
  name: 'ActivityForm',

  props: {
    propActivity: {
      type: Object,
      default: undefined
    },
    contract: {
      type: Object,
      default: undefined
    },
    businessEntities: {
      type: Array,
      default: undefined
    },
    editing: {
      type: Boolean,
      default: false
    },
    isForScheduledRateChange: {
      type: Boolean,
      default: false
    },
    contractActivityChangeBuilders: {
      type: Array,
      default: () => []
    },
    namedRates: {
      type: Array,
      default: undefined
    }
  },

  components: {
    BaseCostForm: () => import('@/components/activity/BaseCostForm.vue'),
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    AdvanceAutocomplete: () => import('@/components/autocomplete/AdvanceAutocomplete.vue'),
    TemplateAutocomplete: () => import('@/components/autocomplete/TemplateAutocomplete.vue'),
    ActivityCostWarning: () => import('@/components/activity/ActivityCostWarning.vue'),
    ActivityFormContractHeader: () => import('@/components/activity/ActivityFormContractHeader.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    TemplateSpecialization,
    TemplateAccountingCategory,
    TemplateSpecializationInfo,
    rules: FieldRules.default.rules,
    maxBaseRate: FieldRules.MAX_ACTIVITY_RATE,
    costWarning: false,
    datePickerError: false,
    templateError: false,
    accountError: false,
    selectedBusinessEntity: undefined,
    isBaseCost: false,
    firstEditLoad: false,
    accountAdvances: [],
    activity: {
      baseCost: {},
      contractor: {},
      activityTemplate: {},
      advanceNote: null,
      advanceId: null,
      recoveryMode: RecoveryMode.None,
      effectiveDate: moment.utc(),
      deductingActivities: [],
      runtimeCounterparty: null
    },
    rateStats: undefined,
    tract: undefined,
    ContractType,
    AccountingCategory,
    RecoveryMode,
    landowners: [],
    RecoveryStrategy
  }),

  computed: {
    ...mapGetters('user-settings', ['mutatedUserSettings', 'userSettings']),
    ...mapGetters('user', ['companyInfo']),

    title () {
      return this.editing ? this.$t('editActivity') : this.$t('createActivity')
    },

    contractType () {
      return this.contract?.type
    },

    isStumpageTract () {
      return this.tract?.type?.category === TractTypeCategory.Stumpage.value
    },

    includeDepletion () {
      return this.tract?.type?.specialization === TractTypeSpecialization.Depletion.value
    },

    contractTypeColor () {
      return colorClassForContractType(this.contractType)
    },

    mappedRecoverymodes () {
      return RecoveryMode.enums.map(recoveryMode => ({
        ...recoveryMode,
        name: recoveryMode.name,
        definition: recoveryMode.explanation,
        excluded: (recoveryMode.value === RecoveryMode.Specific.value && (this.activityIsRuntimeCounterparty || !this.activity.contractor?.hasAdvances)) ||
          (recoveryMode.value === RecoveryMode.OldestOnTract.value && (this.contract.tract === null))
      })).filter(recoveryMode => !recoveryMode.excluded)
    },

    activityIsRuntimeCounterparty () {
      return this.activity.runtimeCounterparty !== null && this.activity.runtimeCounterparty !== undefined
    },

    counterpartyString () {
      if (this.dynamicCounterpartyNotSet) return this.$t('notSet')
      return this.counterpartyStringForActivity(this.activity)
    },

    dynamicCounterpartyNotSet () {
      if (this.activity.runtimeCounterparty === null || this.activity.runtimeCounterparty === undefined) return false
      if (this.activity.runtimeCounterparty === RuntimeCounterparty.Landowner.value && this.landowners.length > 0) return false
      if (this.activity.runtimeCounterparty === RuntimeCounterparty.Driver.value) return false
      return this.activity.contractor === null || this.activity.contractor === undefined
    },

    showRecoveryModeSelection () {
      const contractsThatAcceptAdvances = [
        ContractType.Production.value,
        ContractType.WoodsSale.value,
        ContractType.ByproductSale.value,
        ContractType.ByproductPurchase.value
      ]

      return this.activity?.activityTemplate?.category === AccountingCategory.Payable.value && contractsThatAcceptAdvances.includes(this.contractType) &&
        this.activity?.activityTemplate?.specialization !== TemplateSpecialization.Penalty.value
    },

    showBaseCost () {
      return this.isBaseCost &&
      this.activity.activityTemplate !== undefined &&
      this.activity.activityTemplate.specialization !== TemplateSpecialization.Transfer.value &&
      this.activity.activityTemplate.specialization !== TemplateSpecialization.Obligation.value &&
      (this.activity.activityTemplate.specialization !== TemplateSpecialization.Penalty.value ||
        this.activity.activityTemplate.category === AccountingCategory.Receivable.value)
    },

    isButtonDisabled () {
      return !this.activity?.activityTemplate?.activityTemplateId ||
        (!this.activityIsRuntimeCounterparty && !this.activity?.contractor?.accountId)
    },

    accountingCategory () {
      return TemplateAccountingCategory.forInt(this.activity?.activityTemplate?.category)
    },

    deductionEligibleActivities () {
      return this.contractActivityChangeBuilders.filter(a => {
        const businessEntitySame = a.activityTemplate.businessEntityId === this.activity.activityTemplate.businessEntityId
        const activitiesMatch = !this.activitiesMatch(a, this.activity)
        const category = a.activityTemplate.category === AccountingCategory.Payable.value
        const unspecialized = a.activityTemplate.specialization === TemplateSpecialization.None.value
        const production = a.activityTemplate.costType === CostType.Production.value
        const noRecovery = a.recoveryMode === RecoveryMode.None.value
        return businessEntitySame && activitiesMatch && category && unspecialized && production && noRecovery
      })
    },

    canBeDeducted () {
      const anotherActivityAlreadyDeducts = this.contractActivityChangeBuilders.some(a => {
        if (this.activitiesMatch(a, this.activity)) return false
        return a.deductFromActivityId != null
      })
      const thisActivityAlreadyHasDeductions = this.hasDeductingActivities
      return this.contractType === ContractType.Production.value &&
        !this.isStumpageTract &&
        this.accountingCategory === TemplateAccountingCategory.AccountsPayable &&
        this.activity.deductFromActivityId == null &&
        this.activity.activityTemplate.costType === CostType.Production.value &&
        (!anotherActivityAlreadyDeducts || thisActivityAlreadyHasDeductions)
    },

    deductionText () {
      const parentActivity = this.contractActivityChangeBuilders.find(a => a.activityId === this.propActivity?.deductFromActivityId)
      const counterpartyString = this.counterpartyStringForActivity(parentActivity)
      return this.$t('deductsFrom', { from: `${parentActivity.activityTemplate.name} - ${counterpartyString}` })
    },

    activityDeducts () {
      return this.propActivity?.deductFromActivityId != null
    },

    hasDeductingActivities () {
      return (this.activity?.deductingActivities || []).length > 0
    },

    activityTemplateMutable () {
      return !this.activityDeducts && !this.hasDeductingActivities
    },

    isDistanceTraveledActivity () {
      return this.activity?.activityTemplate?.modifier === ActivityModifier.DistanceTraveled.value
    },

    distanceTraveledIndicatorText () {
      return this.contract?.distance > 0
        ? this.$tc('paysOnDistanceTraveledWithMiles', this.contract.distance, { distance: this.contract.distance })
        : this.$t('paysOnDistanceTraveled')
    },

    baseCostPayByTypes () {
      if (!this.isDistanceTraveledActivity) return undefined
      const isReceivable = this.activity?.activityTemplate?.category === AccountingCategory.Receivable.value
      return PayBy.enums.filter(payBy =>
        payBy !== PayBy.Load &&
        payBy !== PayBy.Volume &&
        (payBy !== PayBy.MetricWeight || isReceivable)
      )
    }
  },

  async created () {
    if (this.editing) {
      this.activity = Object.assign(this.activity, cloneDeep(this.propActivity))
      this.activity.activityTemplate.baseCost = new BaseCost(cloneDeep(this.propActivity.baseCost))
      this.activity.effectiveDate = moment.utc()
      this.selectedBusinessEntity = this.businessEntities.find(entity => entity.businessEntityId === this.propActivity.activityTemplate.businessEntityId).name
      this.isBaseCost = true
      this.firstEditLoad = true
      this.activity.recoveryMode = RecoveryMode.forInt(this.propActivity.recoveryMode)
      this.activity.deductingActivities = this.contractActivityChangeBuilders
        .filter(a => a.deductFromActivityId === this.activity.activityId)
        .map(a => a.activityId)
    } else {
      this.selectedBusinessEntity = this.businessEntities[0]?.name
    }
    if (this.contract.tractId) {
      this.landowners = await this.fetchTractLandowners(this.contract.tractId)
      this.tract = await this.fetchTract(this.contract.tractId)
    }
  },

  beforeDestroy () {
    this.updateUserSettings(this.mutatedUserSettings)
  },

  methods: {
    ...mapActions('activity', ['updateActivity', 'createActivity']),
    ...mapActions('activity-detail', ['updateActivityDetail']),
    ...mapActions('user-settings', ['updateUserSettings']),
    ...mapActions('activity-templates', ['fetchActivityTemplateStatsById']),
    ...mapActions('tract', ['fetchTract', 'fetchTractLandowners']),

    wait (timeToDelay) { return new Promise((resolve) => setTimeout(resolve, timeToDelay)) },
    runtimeCounterparty: (val) => RuntimeCounterparty.fromInt(val),
    accountChosen (account) {
      this.activity.contractor = account
      if (this.propActivity?.contractor?.accountId === account?.accountId) {
        this.activity.recoveryMode = RecoveryMode.forInt(this.propActivity.recoveryMode)
        this.activity.advanceId = this.propActivity.advanceId
        return
      }

      this.activity.advanceId = null
      const recoversByDefault = (this.activityIsRuntimeCounterparty || account?.hasAdvances) &&
        (this.activity.activityTemplate.category === AccountingCategory.Payable.value) &&
        (this.contract.type !== ContractType.Transfer.value && this.contract.type !== ContractType.LogYardSale.value)
      this.activity.recoveryMode = recoversByDefault ? RecoveryMode.Oldest : RecoveryMode.None
    },
    baseCostChanged (baseCost) { this.activity.baseCost = baseCost },
    /*
    Base cost refreshes when template is changed. The wait function gives
    time for the component to rerender with the new template's values. Since
    the first edit gets the value from the prop it doesn't need the wait.
    */
    async templateChosen (template) {
      this.isBaseCost = false
      if (this.firstEditLoad) this.firstEditLoad = false
      else await this.wait(0)
      if (template.baseCost !== null) {
        template.baseCost = new BaseCost(template.baseCost)
      }
      this.activity.activityTemplate = template
      if (template === undefined) return
      this.isBaseCost = true
      this.rateStats = await this.fetchActivityTemplateStatsById(template.activityTemplateId)
      if (template.category === AccountingCategory.Accrual.value) this.activity.runtimeCounterparty = null
      if (template.specialization === TemplateSpecialization.Obligation.value) {
        const namedRate = this.namedRates?.find(nr => nr.specialization === NamedRateSpecialization.Obligation.value)
        if (!namedRate) return
        this.activity.baseCost = await BaseCost.fromNamedRate(namedRate)
        this.activity.namedRateId = namedRate.namedRateId
      }
    },
    recoveryTypeAllowedForPayBy,
    advanceChosen (advance) {
      this.activity.advanceId = advance ? advance.advanceId : null
    },

    saveChanges () {
      if (!this.validate()) {
        return
      }

      if (this.activity.baseCost?.payBy == null) this.activity.baseCost = null

      this.$emit('activity-updated', this.activity)
    },

    validate () {
      if (this.activity.activityTemplate.name === undefined) {
        this.templateError = !this.templateError
        this.setSnackError(this.$t('noTemplateChosen'))
        return false
      }

      if (!this.activity.contractor?.name && (this.activity.runtimeCounterparty === null || this.activity.runtimeCounterparty === undefined)) {
        this.accountError = !this.accountError
        this.setSnackError(this.$t('noAccountChosen'))
        return false
      }

      if (this.activity.runtimeField === undefined &&
        this.activity.contractor?.exportCode === '' &&
        this.activity?.activityTemplate?.category !== AccountingCategory.Accrual.value &&
        this.companyInfo.financialIntegrationType !== FinancialIntegrationType.None.value &&
        !this.contract?.isDraft) {
        this.accountError = !this.accountError
        this.setSnackError(this.$t('counterpartyExportCodeErrorActivity', { accountName: this.activity.contractor.name }))
        return false
      }

      if (this.showBaseCost && this.activity.baseCost.rate > this.maxBaseRate) {
        this.templateError = !this.templateError
        this.setSnackError(this.$t('invalidActivityRate'))
        return false
      }

      return true
    },

    getRequestObject () {
      const baseRequestObj = {
        baseCost: this.showBaseCost ? this.activity.baseCost : null,
        effectiveDate: moment.utc(),
        contractorId: this.activity.contractor?.accountId,
        contractId: this.contract.contractId,
        templateId: this.activity.activityTemplate.activityTemplateId,
        advanceId: this.showRecoveryModeSelection && this.activity.recoveryMode.value !== RecoveryMode.None.value ? this.activity.advanceId : null,
        activityId: this.editing ? this.activity.activityId : null,
        recoveryMode: this.showRecoveryModeSelection ? this.activity.recoveryMode.value ?? RecoveryMode.None.value : RecoveryMode.None.value,
        runtimeField: undefined
      }
      if (this.activityIsRuntimeCounterparty) {
        delete baseRequestObj.contractorId
        baseRequestObj.runtimeCounterparty = this.propActivity.runtimeCounterparty
      }
      return baseRequestObj
    },

    handleWarningResult (updateAnyway) {
      if (updateAnyway) {
        this.updateExistingActivity(this.getRequestObject())
      } else {
        this.costWarning = false
      }
    },

    switchToRuntimeCounterparty () {
      this.activity.runtimeCounterparty = this.propActivity.runtimeCounterparty
      this.activity.contractor = this.propActivity.contractor
      this.activity.recoveryMode = this.activity.activityTemplate.category === AccountingCategory.Payable.value ? RecoveryMode.Oldest : RecoveryMode.None
    },

    deductingActivityText (activity) {
      return `${activity.activityTemplate.name}, ${this.counterpartyStringForActivity(activity)}`
    },

    counterpartyStringForActivity (activity) {
      if (activity.contractor?.name) return activity.contractor.name
      switch (activity.runtimeCounterparty) {
        case RuntimeCounterparty.Landowner.value:
          return this.tract?.landOwnerAccountName || this.$t('multipleLandowners')
        case RuntimeCounterparty.Driver.value:
          return this.$t('ticketDriver')
        default:
          return this.$t('notAvailable')
      }
    },

    activitiesMatch (a, b) {
      if (a.activityId && b.activityId) {
        return a.activityId === b.activityId
      } else {
        return a.activityTemplate?.activityTemplateId === b.activityTemplate?.activityTemplateId &&
            a.contractor?.accountId === b.contractor?.accountId &&
            a.runtimeCounterparty === b.runtimeCounterparty
      }
    },

    getItemValue (item) {
      return JSON.stringify({
        activityId: item.activityId,
        activityTemplateId: item.activityTemplate?.activityTemplateId,
        contractorId: item.contractor?.accountId,
        runtimeCounterparty: item.runtimeCounterparty
      })
    }
  }
}
</script>
